import {
    memo,
    useState,
    useEffect,
} from 'react';
import { Row, Col, Form, Button, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { Subscriber } from '../../services/Ros/Subscriber';
import { MapContainer } from '../../core/Maps/MapContainer';
import { GPS_TOPIC } from '../../constants/topics';
import { RobotTrackingMarker } from '../../core/Maps/RobotTrackingMarker';
import { ConnectionMessage } from '../../core/Messages/ConnectionMessage';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { useFetch } from '../../services/Requests/useFetch';
import { useObserver } from '../../utilities/http/routing/ObserverContext';
import { LogViewer } from '../LogViewer';
import { useOperator } from '../../utilities/http/routing/OperatorContext';
import { useEmail } from '../../utilities/http/routing/EmailContext';
import { useMaintenanceMode } from '../../utilities/http/routing/MaintenanceContext';


const defaultSettings = {
    email: '',
    operatorEmail: '',
    imageReportEmail: '',
    activeHours: false,
    startTime: '07:00',
    stopTime: '20:00',
    Operating: false,
    Maintenance: false
};

interface LoadSettings {
    email: string;
    operatorEmail: string;
    imageReportEmail: string;
    activeHours: boolean;
    startTime: string;
    stopTime: string;
    Operating: boolean;
    Maintenance: boolean;
}

const options: RequestInit = {
    method: "GET"
}
const postOptions: RequestInit = {
    method: "POST"
}

export const SettingsPageBodyMemo = () => {
    const [settings, setSettings] = useState(defaultSettings);
    const [showClearModal, setShowClearModal] = useState(false);
    const { isObserver } = useObserver();
    const { isOperator } = useOperator();
    const { setMaintenanceModeEnabled, canMaintain } = useMaintenanceMode();
    const { userEmail } = useEmail();
    const { state: loadState, get: loadSettings, abortRequest: loadAbort } = useFetch<LoadSettings>('/api/missions/load_settings', options);
    const { state: saveState, post: saveSettings } = useFetch<LoadSettings>('/api/missions/save_settings', postOptions);
    const cleardata = useFetch('/api/missions/clear_data', options);

    const toggleMaintenance = useFetch('/api/missions/toggle_maintenance', postOptions);

    console.log(userEmail)
    

    useEffect(() => {
        loadSettings();
        return () => {
            if (loadAbort) {
                loadAbort();
            }
        };
    }, []);

    useEffect(() => {
        const { status, data } = loadState;
        console.log(status)
        if (status === 'fetched' && data) {
            setSettings(data);
            setMaintenanceModeEnabled(data.Maintenance)

        }
    }, [loadState]);

    const handletoggleMaintenance = async () => {
        if (isObserver && !canMaintain){
          console.log(`Operator is an Observer, unable to toggle Maintenance`)
          return
        }
        await toggleMaintenance.post({user: userEmail})
        await loadSettings()
      }

    const handleSave = async () => {
        try {
            await saveSettings(settings);
            loadSettings(); // Refresh settings after saving
        } catch (error) {
            console.error('Error saving settings:', error);
        }
    };

    const handleChange = (key: string, value: any) => {
        setSettings((prevSettings) => ({
            ...prevSettings,
            [key]: value,
        }));
        console.log(saveState)
    };

    const handleClear = () => {
        cleardata.get();
        console.log('Clearing data');
        setShowClearModal(false);
    };

    const handleShowClearModal = () => {
        setShowClearModal(true);
    };

    const handleCloseClearModal = () => {
        setShowClearModal(false);
    };

    if (loadState.status === 'fetching') {
        return <ConnectionMessage icon={faGear} text='Settings Loading' colour='dark' />
    }else if (loadState.status === 'error'){
        return <ConnectionMessage icon={faGear} text='Settings Could Not Load' colour='dark' />
    }

    return (
        <>
            <Row className='h-100'>
                <Col lg={3} className="ps-0">
                    <Subscriber name={GPS_TOPIC.name} type={GPS_TOPIC.type} rate={GPS_TOPIC.throttle}>
                        <MapContainer
                            id="path-planning-map"
                            zoom={18}
                            center={{ lat: -40.379712032272984, lng: 175.61294976361017 }}
                        >
                            {settings.activeHours && <RobotTrackingMarker title="Robot" centerOnRobot={false}/>}
                        </MapContainer>
                    </Subscriber>
                </Col>
                <Col lg={8} className="mt-3 mb-3 path-planning-column">
                    <Form>
                    <Form.Group controlId="emailPreferences">
                        <Form.Label style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Email Preferences</Form.Label>
                        <Form.Text muted> ({userEmail})</Form.Text>

                        {/* Mission Notifications */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <div>
                                <OverlayTrigger
                                    placement="left-start"
                                    overlay={<Tooltip id="tooltip-mission-email">Enable email notifications about mission details and updates.</Tooltip>}
                                >
                                    <Form.Label style={{ marginBottom: 0 }}>Mission Notifications</Form.Label>
                                </OverlayTrigger>
                                
                            </div>

                            <Form.Check
                                type="switch"
                                className="email-switch"
                                checked={settings.email.split(',').map(email => email.trim()).includes(userEmail)}
                                onChange={(e) => {
                                    const emailList = settings.email
                                        .split(',')
                                        .map(email => email.trim())
                                        .filter(email => email.length > 0);

                                    if (e.target.checked && !emailList.includes(userEmail)) {
                                        emailList.push(userEmail);
                                    } else if (!e.target.checked) {
                                        const index = emailList.indexOf(userEmail);
                                        if (index > -1) emailList.splice(index, 1);
                                    }

                                    handleChange('email', emailList.join(', '));
                                }}
                            />
                        </div>
                        <Form.Text className="text-muted mb-2 d-block">
                            Receive mission details and updates at your email.
                        </Form.Text>

                        {/* Operator Notifications */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <div>
                                <OverlayTrigger
                                    placement="left-start"
                                    overlay={<Tooltip id="tooltip-operator-email">Enable operator notifications for battery status and robot connection info.</Tooltip>}
                                >
                                    <Form.Label style={{ marginBottom: 0 }}>Operator Notifications</Form.Label>
                                </OverlayTrigger>
                                {/* <Form.Text muted> ({userEmail})</Form.Text> */}
                            </div>

                            <Form.Check
                                type="switch"
                                className="email-switch"
                                checked={settings.operatorEmail.split(',').map(email => email.trim()).includes(userEmail)}
                                onChange={(e) => {
                                    const emailList = settings.operatorEmail
                                        .split(',')
                                        .map(email => email.trim())
                                        .filter(email => email.length > 0);

                                    if (e.target.checked && !emailList.includes(userEmail)) {
                                        emailList.push(userEmail);
                                    } else if (!e.target.checked) {
                                        const index = emailList.indexOf(userEmail);
                                        if (index > -1) emailList.splice(index, 1);
                                    }

                                    handleChange('operatorEmail', emailList.join(', '));
                                }}
                            />
                        </div>
                        <Form.Text className="text-muted mb-2 d-block">
                            Receive operator-specific emails like battery and connection alerts.
                        </Form.Text>

                        {/* Image Report Notifications */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <OverlayTrigger
                                    placement="left-start"
                                    overlay={<Tooltip id="tooltip-image-report-email">Enable image report emails after each mission.</Tooltip>}
                                >
                                    <Form.Label style={{ marginBottom: 0 }}>Image Report</Form.Label>
                                </OverlayTrigger>
                            </div>

                            <Form.Check
                                type="switch"
                                className="email-switch"
                                checked={settings.imageReportEmail.split(',').map(email => email.trim()).includes(userEmail)}
                                onChange={(e) => {
                                    const emailList = settings.imageReportEmail
                                        .split(',')
                                        .map(email => email.trim())
                                        .filter(email => email.length > 0);

                                    if (e.target.checked && !emailList.includes(userEmail)) {
                                        emailList.push(userEmail);
                                    } else if (!e.target.checked) {
                                        const index = emailList.indexOf(userEmail);
                                        if (index > -1) emailList.splice(index, 1);
                                    }

                                    handleChange('imageReportEmail', emailList.join(', '));
                                }}
                            />
                        </div>
                        <Form.Text className="text-muted d-block">
                            Get image reports after each mission to your email.
                        </Form.Text>
                    </Form.Group>
                    <br></br>
                    <Button variant="primary" onClick={handleSave}>
                                Save
                    </Button>
                    {!isObserver && (
                        <>
                            <br></br>
                            <Button variant="danger"  onClick={handleShowClearModal}>
                                Clear Data
                            </Button>
                        </>
                    )}

                    {(!isObserver || canMaintain) &&<Button variant={settings.Operating ? "danger" : "warning"} className="flex-fill m-2" onClick={() => handletoggleMaintenance()} disabled={loadState.status !== "fetched"}>
                                  {settings.Maintenance ? "Disable Maintenance Mode" : "Enable Maintenance Mode"}
                    </Button>}

                    </Form>
                    <br/>
                    {isOperator && <LogViewer/>}
                </Col>
                {/* <Col lg={6} className="ps-0">
                    
                </Col> */}
                
            </Row>
            

            <Modal show={showClearModal} onHide={handleCloseClearModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Clear Data</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to clear all data including assets, missions, bus stops, and routes?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseClearModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleClear}>
                        Clear Data
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export const SettingsPageBody = memo(SettingsPageBodyMemo);
